// Demo mode in the browser (PD-714 owner instruction; demoguard stream PD-729, PD-730). The API is
// the authority: it refuses every change with DEMO_READ_ONLY and says so in /auth/context
// (`demo: true`). Here: a persistent banner on every surface (staff app, system console, visitor
// portal, kiosk) and one uniform, polite notice when a change was refused — the page stays where it
// is and every form keeps what the visitor typed, so buttons and dialogs can still be explored.
// PD-763 / PD-776 (demosandbox stream): kiosk and visitor-pass answers appear to work in a
// per-browser sandbox; the same notice then says truthfully that it worked for this browser only and
// is undone after the idle time — or, at the sandbox cap, that it was shown once and not kept.
import type { AuthContextResponse } from '@passage/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { DEMO_READ_ONLY_EVENT, DEMO_SANDBOX_EVENT, type DemoSandboxNote } from '../api.ts';
import { useI18n } from '../i18n.tsx';

/**
 * PD-776: whether this installation runs in demo mode (from /auth/context, which Root loads before any
 * surface renders). Pages use it to keep their own copy truthful (nobody is told, nothing is kept).
 */
export function useIsDemo(): boolean {
  return useQueryClient().getQueryData<AuthContextResponse>(['auth-context'])?.demo === true;
}

/** Persistent, non-dismissable banner. A labelled region, so screen readers can find it. */
export function DemoBanner() {
  const { t } = useI18n();
  return (
    <div className="demo-banner" role="region" aria-label={t('demo.bannerRegion')} data-demo-banner>
      <strong>{t('demo.banner')}</strong>
    </div>
  );
}

const NOTICE_MS = 8000;

/** What the notice says: a refused change, or an answer that worked in this browser's sandbox. */
export type DemoNoticeKind = { readonly kind: 'refused' } | { readonly kind: 'sandbox'; readonly note: DemoSandboxNote };

/** The notice text (rendered with react-dom/server in tests). */
export function DemoNoticeBody({ notice, onDismiss }: { notice: DemoNoticeKind; onDismiss: () => void }) {
  const { t } = useI18n();
  const [title, body] =
    notice.kind === 'refused'
      ? [t('demo.notice.title'), t('demo.notice.body')]
      : notice.note.state === 'fallback'
        ? [t('demo.sandbox.fallbackTitle'), t('demo.sandbox.fallbackBody')]
        : [t('demo.sandbox.title'), t('demo.sandbox.body', { minutes: Math.max(1, Math.round(notice.note.idleSeconds / 60)) })];
  return (
    <div className="demo-notice" data-demo-notice={notice.kind === 'refused' ? 'refused' : notice.note.state}>
      <div>
        <strong>{title}</strong>
        <span>{body}</span>
      </div>
      <button className="button quiet small" type="button" onClick={onDismiss}>
        {t('demo.notice.dismiss')}
      </button>
    </div>
  );
}

/** Announces a refused change or a sandboxed answer (polite live region); dismissable, fades after a few seconds. */
export function DemoNotice() {
  const [shown, setShown] = useState<{ n: number; notice: DemoNoticeKind } | null>(null);
  useEffect(() => {
    const refused = () => setShown((s) => ({ n: (s?.n ?? 0) + 1, notice: { kind: 'refused' } }));
    const sandbox = (e: Event) => {
      const note = (e as Event & { detail?: DemoSandboxNote }).detail;
      if (note) setShown((s) => ({ n: (s?.n ?? 0) + 1, notice: { kind: 'sandbox', note } }));
    };
    window.addEventListener(DEMO_READ_ONLY_EVENT, refused);
    window.addEventListener(DEMO_SANDBOX_EVENT, sandbox);
    return () => {
      window.removeEventListener(DEMO_READ_ONLY_EVENT, refused);
      window.removeEventListener(DEMO_SANDBOX_EVENT, sandbox);
    };
  }, []);
  useEffect(() => {
    if (!shown) return;
    const timer = setTimeout(() => setShown(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [shown]);
  return (
    <div className="demo-notice-host" role="status" aria-live="polite">
      {shown ? <DemoNoticeBody notice={shown.notice} onDismiss={() => setShown(null)} /> : null}
    </div>
  );
}
